<template>
    <div v-if="params.data" class="flex gap-2">
        <template v-if="invoiceUuid && subAccountUuid" >
            <inv-link-resolver
                :uuid="invoiceUuid"
                :invoice-no="transactionLine.invoiceNo"
                :sub-account-uuid="subAccountUuid"
            />
            /
        </template>
        <template v-if="subAccountUuid">
            <sacc-link-resolver
                :uuid="subAccountUuid"
                :sub-account-name="transactionLine.subAccountName"
                :is-customer="isCustomer"
            />
            /
        </template>
        <template v-if="singleCrossSubAccount">
            <sacc-link-resolver
                :uuid="singleCrossSubAccount.uuid"
                :sub-account-name="singleCrossSubAccount.name"
                :is-customer="singleCrossSubAccount.relationType === CUSTOMER"
            />
            /
        </template>
        <span :class="{'line-through text-error': params.data?.deleted}">
            {{ transactionLine.description }}
        </span>
    </div>
</template>

<script setup lang="ts">

import {TransactionLine} from '@/types/transaction/TransactionLine'
import {computed, provide, ref, watch} from 'vue'
import InvLinkResolver from '@/textTokenizer/InvLinkResolver.vue'
import SaccLinkResolver from '@/textTokenizer/SaccLinkResolver.vue'
import {CUSTOMER_ACCOUNT_NO} from '@/bookkeeping-account/accountTypes'
import {getTransactionByUuid} from '@/transaction/transaction.service'
import {Transaction} from '@/types/transaction/Transaction'
import {CUSTOMER, SubAccount} from '@/subaccount/types/SubAccount'
import {findSubAccountByUuid} from '@/subaccount/subAccount.service'
import _ from 'lodash'

const props = defineProps({
    params: {
        type: Object as () => {
            data: TransactionLine,
            context: any
        },
        required: true
    }
})

provide('panelId', props.params.context.panelId)
provide('panelPathSegment', props.params.context.panelPathSegment)

const transaction = ref<Transaction>()

const transactionLine = computed(() => {
    return props.params.data
})

const isCustomer = computed(() => {
    return transactionLine.value.accountNo === CUSTOMER_ACCOUNT_NO
})

const subAccountUuid = computed(() => {
    return transactionLine.value.subAccountUuid
})

const crossSubAccounts = computed(() => {
    return _(transaction.value?.transactionLines || [])
        .filter(line => line.entryLineIndex === transactionLine.value.entryLineIndex)
        .map(line => line.subAccountUuid)
        .uniq()
        .filter(uuid => !!uuid)
        .filter(uuid => uuid !== subAccountUuid.value)
        .value()
})

const singleCrossSubAccount = computed<SubAccount | undefined>(() => {
    if (crossSubAccounts.value && crossSubAccounts.value.length === 1) {
        return findSubAccountByUuid(crossSubAccounts.value[0]!)
    }
    return undefined
})

const invoiceUuid = computed(() => {
    return transactionLine.value.invoiceUuid
})

const transactionUuid = computed(() => {
    return transactionLine.value?.transactionUuid
})

watch(transactionUuid, async () => {
    if (transactionUuid.value) {
        const transaction_ = await getTransactionByUuid(props.params?.data.transactionUuid)
        if (transaction_) {
            transaction.value = transaction_
        }
    }
}, {immediate: true})

</script>

<style scoped>

</style>